import type {
  EditTimeReportData,
  TimeReportsV2Item,
} from "services/api/company/timeReports.service";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";
import {
  isReportedTimeWindowValid,
  mergeIsoDateWithTimeInput,
  minutesBetweenIsoStrict,
  normalizeTimeReportTimesOvernight,
  parseIsoMillis,
  toTimeInputValue,
} from "web-apps/company/utils/timeAndDate";

export interface InlineTimeReportDraft {
  readonly breakEnabled: boolean;
  readonly breakMinutes: string;
  readonly end: string;
  readonly start: string;
}

export type InlineTimeReportError = "incomplete-times" | "invalid-times";

interface InlineTimeReportDraftChangesInput {
  readonly baseline: InlineTimeReportDraft;
  readonly draft: InlineTimeReportDraft;
}

export function hasInlineTimeReportDraftChanges({
  baseline,
  draft,
}: InlineTimeReportDraftChangesInput): boolean {
  return (
    draft.start !== baseline.start ||
    draft.end !== baseline.end ||
    draft.breakEnabled !== baseline.breakEnabled ||
    (draft.breakEnabled && draft.breakMinutes !== baseline.breakMinutes)
  );
}

export function createInlineTimeReportDraft(
  item: TimeReportsV2Item,
): InlineTimeReportDraft {
  const breakMinutes =
    minutesBetweenIsoStrict(item.breakStartTime, item.breakEndTime) ?? 0;
  return {
    start: toTimeInputValue(item.reportedStartTime),
    end: toTimeInputValue(item.reportedEndTime),
    breakMinutes: String(breakMinutes),
    breakEnabled: breakMinutes > 0,
  };
}

interface InlineTimeReportPayloadInput {
  readonly draft: InlineTimeReportDraft;
  readonly item: TimeReportsV2Item;
}

export function resolveInlineTimeReportPayload({
  draft,
  item,
}: InlineTimeReportPayloadInput): Result<
  EditTimeReportData,
  InlineTimeReportError
> {
  if (!draft.start || !draft.end) return err("incomplete-times");
  const base = item.reportedStartTime ?? item.plannedStartTime;
  // Keep precision and the original date for fields that were not edited.
  const startTime =
    draft.start === toTimeInputValue(item.reportedStartTime)
      ? item.reportedStartTime
      : mergeIsoDateWithTimeInput(base, draft.start);
  const endTime =
    draft.end === toTimeInputValue(item.reportedEndTime)
      ? item.reportedEndTime
      : mergeIsoDateWithTimeInput(startTime, draft.end);
  if (!startTime || !endTime || startTime === endTime)
    return err("invalid-times");
  const times = normalizeTimeReportTimesOvernight({
    startTime,
    endTime,
    breakStartTime: startTime,
    breakEndTime: startTime,
  });
  if (!times) return err("invalid-times");
  const payload = resolveBreakMinutesPayload({ item, draft, times });
  if (!payload.ok) return payload;
  return isReportedTimeWindowValid(payload.value)
    ? payload
    : err("invalid-times");
}

interface BreakMinutesPayloadInput extends InlineTimeReportPayloadInput {
  readonly times: EditTimeReportData;
}

interface BreakPlacementInput extends BreakMinutesPayloadInput {
  readonly minutes: number;
}

interface BreakCandidateInput {
  readonly minutes: number;
  readonly startMillis: number;
  readonly times: EditTimeReportData;
}

function resolveBreakMinutesPayload({
  draft,
  item,
  times,
}: BreakMinutesPayloadInput): Result<
  EditTimeReportData,
  InlineTimeReportError
> {
  const minutes = draft.breakEnabled ? Number(draft.breakMinutes) : 0;
  const duration = minutesBetweenIsoStrict(times.startTime, times.endTime);
  if (
    (draft.breakEnabled && !/^\d+$/.test(draft.breakMinutes)) ||
    !Number.isSafeInteger(minutes) ||
    minutes < 0 ||
    duration == null ||
    minutes >= duration
  )
    return err("invalid-times");
  return resolveBreakPlacement({ draft, item, minutes, times });
}

function resolveBreakPlacement({
  draft,
  item,
  minutes,
  times,
}: BreakPlacementInput): Result<EditTimeReportData, InlineTimeReportError> {
  const existingTimes =
    item.breakStartTime && item.breakEndTime
      ? {
          ...times,
          breakStartTime: item.breakStartTime,
          breakEndTime: item.breakEndTime,
        }
      : null;
  if (
    draft.breakEnabled &&
    existingTimes &&
    isReportedTimeWindowValid(existingTimes) &&
    minutes === minutesBetweenIsoStrict(item.breakStartTime, item.breakEndTime)
  ) {
    return ok(existingTimes);
  }
  const startMillis = parseIsoMillis(times.startTime);
  if (startMillis == null) return err("invalid-times");
  const candidate = createBreakCandidate({
    minutes,
    startMillis: parseIsoMillis(item.breakStartTime) ?? startMillis,
    times,
  });
  if (candidate.ok && isReportedTimeWindowValid(candidate.value))
    return candidate;
  return createBreakCandidate({ minutes, startMillis, times });
}

function createBreakCandidate({
  minutes,
  startMillis,
  times,
}: BreakCandidateInput): Result<EditTimeReportData, InlineTimeReportError> {
  // toJSON returns null for dates outside JavaScript's range instead of throwing.
  const breakStartTime = new Date(startMillis).toJSON();
  const breakEndTime = new Date(startMillis + minutes * 60000).toJSON();
  if (breakStartTime == null || breakEndTime == null)
    return err("invalid-times");
  return ok({
    ...times,
    breakStartTime,
    breakEndTime,
  });
}

interface InlineTimeReportChangesInput {
  readonly item: TimeReportsV2Item;
  readonly payload: EditTimeReportData;
}

export function hasInlineTimeReportChanges({
  item,
  payload,
}: InlineTimeReportChangesInput): boolean {
  return (
    parseIsoMillis(item.reportedStartTime) !==
      parseIsoMillis(payload.startTime) ||
    parseIsoMillis(item.reportedEndTime) !== parseIsoMillis(payload.endTime) ||
    parseIsoMillis(item.breakStartTime) !==
      parseIsoMillis(payload.breakStartTime) ||
    parseIsoMillis(item.breakEndTime) !== parseIsoMillis(payload.breakEndTime)
  );
}
